import { Link } from "react-router-dom";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import Navigation from "@/components/Navigation";
import SandboxBadge from "@/components/SandboxBadge";
import type { EducationPackage, EducationLevel, Curriculum } from "../types/education";

// Import assets
import heroEducationImg from "@/assets/hero-education.jpg";

const LEVELS: { key: EducationLevel; label: string }[] = [
  { key: "early", label: "Early Years" },
  { key: "primary", label: "Primary" },
  { key: "secondary", label: "Secondary" },
  { key: "higher", label: "Higher Ed" },
];

const CURRICULA: Curriculum[] = ["British", "IB", "American", "CBSE", "French", "Other"];

const FEE_BANDS = [
  { key: "<30k", label: "< 30k", match: (p: EducationPackage) => p.price < 30000 },
  { key: "30-60k", label: "30–60k", match: (p: EducationPackage) => p.price >= 30000 && p.price <= 60000 },
  { key: "60-100k", label: "60–100k", match: (p: EducationPackage) => p.price > 60000 && p.price <= 100000 },
  { key: ">100k", label: "> 100k", match: (p: EducationPackage) => p.price > 100000 },
];

const PACKAGES: EducationPackage[] = [
  // Early Years (Expanded)
  {
    id: "edu-gems-kg2",
    level: "early",
    curriculum: "British",
    title: "GEMS – KG2",
    description: "British EYFS. BNPL for term + finance for remainder.",
    price: 42000,
    termCount: 3,
    image: "/education-gems.jpg",
    duration: "10 months (3 terms)",
    location: "Dubai",
    ratingLabel: "KHDA: Good",
    includes: { transport: false, books: false, uniform: false }
  },
  {
    id: "edu-raffles-nursery",
    level: "early",
    curriculum: "British",
    title: "Raffles Nursery – Foundation 2",
    description: "Premium early years program with specialist teachers.",
    price: 55000,
    termCount: 3,
    image: "/education-gems.jpg",
    duration: "10 months (3 terms)",
    location: "Dubai",
    ratingLabel: "KHDA: Outstanding",
    includes: { transport: true, books: true, uniform: false }
  },
  {
    id: "edu-montessori-kg1",
    level: "early",
    curriculum: "Other",
    title: "Montessori House – KG1",
    description: "Child-centered Montessori approach with multilingual exposure.",
    price: 28000,
    termCount: 3,
    image: "/education-gems.jpg",
    duration: "10 months (3 terms)",
    location: "Abu Dhabi",
    ratingLabel: "ADEK: Good",
    includes: { transport: false, books: true, uniform: true }
  },
  {
    id: "edu-redwood-pre-k",
    level: "early",
    curriculum: "American",
    title: "Redwood Montessori – Pre-K",
    description: "Bilingual Arabic-English early learning program.",
    price: 35000,
    termCount: 3,
    image: "/education-american.jpg",
    duration: "10 months (3 terms)",
    location: "Sharjah",
    includes: { transport: false, books: false, uniform: false }
  },
  
  // Primary (Expanded)
  {
    id: "edu-cbse-class3",
    level: "primary",
    curriculum: "CBSE",
    title: "CBSE – Class 3",
    description: "Annual tuition; enrolment letter required.",
    price: 19000,
    termCount: 3,
    image: "/education-cbse.jpg",
    duration: "10 months (3 terms)",
    location: "Sharjah",
    includes: { transport: false, books: false, uniform: false }
  },
  {
    id: "edu-winchester-year4",
    level: "primary",
    curriculum: "British",
    title: "Winchester School – Year 4",
    description: "British curriculum with Arabic and Islamic studies.",
    price: 32000,
    termCount: 3,
    image: "/education-gems.jpg",
    duration: "10 months (3 terms)",
    location: "Dubai",
    ratingLabel: "KHDA: Very Good",
    includes: { transport: true, books: false, uniform: false }
  },
  {
    id: "edu-aspen-heights-grade5",
    level: "primary",
    curriculum: "American",
    title: "Aspen Heights – Grade 5",
    description: "American curriculum with STEM focus and gifted programs.",
    price: 48000,
    termCount: 2,
    image: "/education-american.jpg",
    duration: "9 months (2 semesters)",
    location: "Al Ain",
    ratingLabel: "ADEK: Very Good",
    includes: { transport: false, books: true, uniform: false }
  },
  {
    id: "edu-dps-class6",
    level: "primary",
    curriculum: "CBSE",
    title: "Delhi Private School – Class 6",
    description: "CBSE curriculum with strong Indian cultural foundation.",
    price: 24000,
    termCount: 3,
    image: "/education-cbse.jpg",
    duration: "10 months (3 terms)",
    location: "Dubai",
    ratingLabel: "KHDA: Good",
    includes: { transport: true, books: false, uniform: true }
  },
  {
    id: "edu-lycee-cm2",
    level: "primary",
    curriculum: "French",
    title: "Lycée Français – CM2",
    description: "French national curriculum following Ministry standards.",
    price: 38000,
    termCount: 3,
    image: "/education-lycee.jpg",
    duration: "10 months (3 terms)",
    location: "Dubai",
    includes: { transport: false, books: false, uniform: false }
  },

  // Secondary (Expanded)
  {
    id: "edu-dia-year-10",
    level: "secondary",
    curriculum: "IB",
    title: "Dubai International Academy – Year 10",
    description: "IB curriculum. Financing shows term-wise split.",
    price: 68000,
    termCount: 3,
    image: "/education-dia.jpg",
    duration: "10 months (3 terms)",
    location: "Dubai",
    ratingLabel: "KHDA: Very Good",
    includes: { transport: false, books: false, uniform: false }
  },
  {
    id: "edu-repton-year-12",
    level: "secondary",
    curriculum: "British",
    title: "Repton – Year 12 (A-Levels)",
    description: "Higher fee band; mixed financing defaults.",
    price: 98000,
    termCount: 3,
    image: "/education-repton.jpg",
    duration: "10 months (3 terms)",
    location: "Dubai",
    ratingLabel: "KHDA: Outstanding",
    includes: { transport: false, books: false, uniform: false }
  },
  {
    id: "edu-ib-dp1",
    level: "secondary",
    curriculum: "IB",
    title: "IB Diploma Programme (DP1)",
    description: "DP1 annual tuition view.",
    price: 90000,
    termCount: 3,
    image: "/education-ibdp.jpg",
    duration: "10 months (3 terms)",
    location: "Dubai",
    includes: { transport: false, books: false, uniform: false }
  },
  {
    id: "edu-american-grade12",
    level: "secondary",
    curriculum: "American",
    title: "American Curriculum – Grade 12 (AP)",
    description: "US/AP track. Mix options.",
    price: 60000,
    termCount: 2,
    image: "/education-american.jpg",
    duration: "9–10 months (2 semesters)",
    location: "Dubai",
    includes: { transport: false, books: false, uniform: false }
  },
  {
    id: "edu-french-lycee-seconde",
    level: "secondary",
    curriculum: "French",
    title: "Lycée Français – Seconde",
    description: "French curriculum. Annual tuition preview.",
    price: 47000,
    termCount: 3,
    image: "/education-lycee.jpg",
    duration: "10 months (3 terms)",
    location: "Dubai",
    includes: { transport: false, books: false, uniform: false }
  },
  {
    id: "edu-jess-year-7",
    level: "secondary",
    curriculum: "British",
    title: "JESS – Year 7",
    description: "British curriculum. Enrolment confirmation required.",
    price: 56000,
    termCount: 3,
    image: "/education-jess.jpg",
    duration: "10 months (3 terms)",
    location: "Dubai",
    includes: { transport: false, books: false, uniform: false }
  },
  {
    id: "edu-kings-year13",
    level: "secondary",
    curriculum: "British",
    title: "King's School – Year 13 A-Levels",
    description: "Premium British education with Oxbridge preparation.",
    price: 115000,
    termCount: 3,
    image: "/education-gems.jpg",
    duration: "10 months (3 terms)",
    location: "Dubai",
    ratingLabel: "KHDA: Outstanding",
    includes: { transport: false, books: true, uniform: false }
  },
  {
    id: "edu-wellington-year11",
    level: "secondary",
    curriculum: "British",
    title: "Wellington International – Year 11 IGCSE",
    description: "IGCSE preparation with extensive extracurriculars.",
    price: 78000,
    termCount: 3,
    image: "/education-gems.jpg",
    duration: "10 months (3 terms)",
    location: "Dubai",
    ratingLabel: "KHDA: Outstanding",
    includes: { transport: false, books: false, uniform: false }
  },
  {
    id: "edu-gems-modern-grade11",
    level: "secondary",
    curriculum: "American",
    title: "GEMS Modern Academy – Grade 11",
    description: "AP courses with college counseling support.",
    price: 52000,
    termCount: 2,
    image: "/education-american.jpg",
    duration: "9 months (2 semesters)",
    location: "Dubai",
    ratingLabel: "KHDA: Very Good",
    includes: { transport: true, books: false, uniform: false }
  },
  {
    id: "edu-cranleigh-year12",
    level: "secondary",
    curriculum: "British",
    title: "Cranleigh Abu Dhabi – Year 12",
    description: "British boarding school tradition with day options.",
    price: 95000,
    termCount: 3,
    image: "/education-gems.jpg",
    duration: "10 months (3 terms)",
    location: "Abu Dhabi",
    ratingLabel: "ADEK: Outstanding",
    includes: { transport: false, books: true, uniform: true }
  },

  // Higher Education (Expanded)
  {
    id: "edu-uni-bba-semesters",
    level: "higher",
    curriculum: "Other",
    title: "University – BBA (Year 1)",
    description: "Semester-based billing. Shows BNPL for next semester.",
    price: 90000,
    termCount: 2,
    image: "/education-uni.jpg",
    duration: "8–9 months (2 semesters)",
    location: "Dubai",
    includes: { transport: false, books: false, uniform: false }
  },
  {
    id: "edu-au-engineering",
    level: "higher",
    curriculum: "Other",
    title: "American University – Engineering Degree",
    description: "ABET-accredited engineering program with internships.",
    price: 125000,
    termCount: 2,
    image: "/education-uni.jpg",
    duration: "8 months (2 semesters)",
    location: "Dubai",
    includes: { transport: false, books: false, uniform: false }
  },
  {
    id: "edu-hult-mba",
    level: "higher",
    curriculum: "Other",
    title: "Hult International – MBA Program",
    description: "One-year intensive MBA with global rotation options.",
    price: 180000,
    termCount: 3,
    image: "/education-uni.jpg",
    duration: "12 months (3 terms)",
    location: "Dubai",
    includes: { transport: false, books: true, uniform: false }
  },
  {
    id: "edu-insead-emba",
    level: "higher",
    curriculum: "Other",
    title: "INSEAD – Executive MBA",
    description: "Part-time EMBA for working professionals.",
    price: 220000,
    termCount: 3,
    image: "/education-uni.jpg",
    duration: "18 months (3 modules)",
    location: "Abu Dhabi",
    includes: { transport: false, books: true, uniform: false }
  },
  {
    id: "edu-uae-medicine",
    level: "higher",
    curriculum: "Other",
    title: "UAE University – Medical College",
    description: "Pre-clinical years of medical degree program.",
    price: 95000,
    termCount: 2,
    image: "/education-uni.jpg",
    duration: "8 months (2 semesters)",
    location: "Al Ain",
    includes: { transport: false, books: true, uniform: false }
  },
  {
    id: "edu-zu-law",
    level: "higher",
    curriculum: "Other",
    title: "Zayed University – Law Degree",
    description: "Bilingual law program with UAE legal system focus.",
    price: 85000,
    termCount: 2,
    image: "/education-uni.jpg",
    duration: "8 months (2 semesters)",
    location: "Dubai",
    includes: { transport: false, books: false, uniform: false }
  },
  {
    id: "edu-rit-computing",
    level: "higher",
    curriculum: "Other",
    title: "RIT Dubai – Computer Science",
    description: "STEM-focused program with industry partnerships.",
    price: 110000,
    termCount: 2,
    image: "/education-uni.jpg",
    duration: "8 months (2 semesters)",
    location: "Dubai",
    includes: { transport: false, books: false, uniform: false }
  },
  {
    id: "edu-manipal-pharmacy",
    level: "higher",
    curriculum: "Other",
    title: "Manipal University – Pharmacy Degree",
    description: "PharmD program with clinical training components.",
    price: 75000,
    termCount: 2,
    image: "/education-uni.jpg",
    duration: "8 months (2 semesters)",
    location: "Dubai",
    includes: { transport: false, books: true, uniform: false }
  },
  {
    id: "edu-sp-jain-masters",
    level: "higher",
    curriculum: "Other",
    title: "SP Jain – Master of Global Business",
    description: "Multi-city program with Dubai, Singapore, Sydney.",
    price: 145000,
    termCount: 3,
    image: "/education-uni.jpg",
    duration: "16 months (3 terms)",
    location: "Dubai",
    includes: { transport: false, books: true, uniform: false }
  },
  {
    id: "edu-cert-cfa",
    level: "higher",
    curriculum: "Other",
    title: "CFA Institute – Chartered Financial Analyst",
    description: "Professional certification preparation program.",
    price: 25000,
    termCount: 2,
    image: "/education-uni.jpg",
    duration: "6 months intensive",
    location: "Dubai",
    includes: { transport: false, books: true, uniform: false }
  },
  {
    id: "edu-cert-pmp",
    level: "higher",
    curriculum: "Other",
    title: "PMI – Project Management Professional",
    description: "PMP certification with practical project experience.",
    price: 18000,
    termCount: 2,
    image: "/education-uni.jpg",
    duration: "4 months weekend",
    location: "Abu Dhabi",
    includes: { transport: false, books: true, uniform: false }
  }
];

export default function Education() {
  const [level, setLevel] = useState<EducationLevel>("early");
  const [curriculum, setCurriculum] = useState<Curriculum | "">("");
  const [emirate, setEmirate] = useState<string>("");
  const [feeBand, setFeeBand] = useState<string>("");

  const filtered = useMemo(() => {
    return PACKAGES.filter(p =>
      p.level === level &&
      (curriculum ? p.curriculum === curriculum : true) &&
      (emirate ? p.location.toLowerCase().includes(emirate.toLowerCase()) : true) &&
      (feeBand ? FEE_BANDS.find(b => b.key === feeBand)?.match(p) : true)
    );
  }, [level, curriculum, emirate, feeBand]);

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <div className="pt-20">
        {/* Hero Section */}
        <section 
          className="relative py-24 px-4 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${heroEducationImg})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-primary/80 via-primary-glow/70 to-accent/60"></div>
          <div className="relative container mx-auto text-center text-white">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">Flex Education</h1>
            <SandboxBadge />
            <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto opacity-95">
              Student must already be enrolled. We finance school-issued invoices — typically BNPL for upcoming term/semester and finance for the remainder.
            </p>
          </div>
        </section>

        <main className="container mx-auto px-4 py-12">
          {/* Level tabs */}
          <div className="flex gap-2 mb-6 overflow-x-auto">
            {LEVELS.map(l => {
              const active = l.key === level;
              return (
                <Button
                  key={l.key}
                  variant={active ? "default" : "outline"}
                  onClick={() => setLevel(l.key)}
                  className="whitespace-nowrap"
                >
                  {l.label}
                </Button>
              );
            })}
          </div>

          {/* Filters */}
          <div className="grid md:grid-cols-4 gap-4 mb-8">
            <select
              className="border border-border rounded-lg p-3 bg-background text-foreground"
              value={curriculum}
              onChange={(e) => setCurriculum(e.target.value as Curriculum | "")}
            >
              <option value="">All Curricula</option>
              {CURRICULA.map(c => <option key={c} value={c}>{c}</option>)}
            </select>

            <input
              className="border border-border rounded-lg p-3 bg-background text-foreground placeholder:text-muted-foreground"
              placeholder="Filter by Emirate/City (e.g., Dubai)"
              value={emirate}
              onChange={(e) => setEmirate(e.target.value)}
            />

            <select
              className="border border-border rounded-lg p-3 bg-background text-foreground"
              value={feeBand}
              onChange={(e) => setFeeBand(e.target.value)}
            >
              <option value="">All Fee Bands</option>
              {FEE_BANDS.map(b => <option key={b.key} value={b.key}>{b.label}</option>)}
            </select>

            <div className="text-sm text-muted-foreground flex items-center">
              Invoice-based financing • Enrolment letter may be requested
            </div>
          </div>

          {/* Cards */}
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map(p => (
              <Card key={p.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                <div className="aspect-video relative overflow-hidden">
                  <img 
                    src={p.image} 
                    alt={p.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                    <span className="bg-primary/10 text-primary px-2 py-1 rounded-full text-xs">
                      {p.curriculum}
                    </span>
                    <span>•</span>
                    <span>{p.location}</span>
                    <span>•</span>
                    <span>{p.duration}</span>
                  </div>
                  {p.ratingLabel && (
                    <div className="text-xs text-muted-foreground mb-2">{p.ratingLabel}</div>
                  )}
                  <h3 className="text-xl font-semibold text-foreground mb-3">{p.title}</h3>
                  <p className="text-muted-foreground text-sm mb-4 line-clamp-2">{p.description}</p>
                  <div className="space-y-2">
                    <div className="text-2xl font-bold text-foreground">
                      AED {p.price.toLocaleString()}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {p.termCount === 3 ? `≈ AED ${Math.round(p.price/3).toLocaleString()} × 3 terms` : `≈ AED ${Math.round(p.price/2).toLocaleString()} × 2 semesters`}
                    </div>
                    <Button asChild className="w-full">
                      <Link to={`/education/course/${p.id}`}>
                        Book Course
                      </Link>
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No courses found matching your filters.</p>
              <Button 
                variant="outline" 
                onClick={() => {
                  setCurriculum("");
                  setEmirate("");
                  setFeeBand("");
                }}
                className="mt-4"
              >
                Clear Filters
              </Button>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export { PACKAGES as EDUCATION_PACKAGES };